import { Stack, Typography } from '@mui/material';
import { SidebarNote, SuggestionItem } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import NoteList from '../../augmented-writing/suggestions/sidebar-notes-list';
import SuggestionCard from '../../augmented-writing/suggestions/suggestion-card';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiSwitch from '../../ui/base-components/pai-switch';

interface Props {
  allSuggestions: SuggestionItem[];
  sidebarNotes: SidebarNote[];
  loading: boolean;

  skipSuggestion(content: any, id: string): () => void;

  ignoreSuggestion(content: any, id: string): () => void;

  insertAtPosition(word: string, pos: number | number[]): () => void;

  onSidebarNotesCategoryClicked(category: any, sidebarNote: SidebarNote, manual?: boolean): void;

  isSuggestionActive(highlightId: string): boolean;

  activateSuggestionCard(highlightId: string): () => void;

  resetEditorHighlights(): void;
}

const TextGenerationSuggestions: React.FC<Props> = ({
  allSuggestions,
  sidebarNotes,
  loading,
  skipSuggestion,
  ignoreSuggestion,
  insertAtPosition,
  onSidebarNotesCategoryClicked,
  isSuggestionActive,
  activateSuggestionCard,
  resetEditorHighlights,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [assistantActive, setAssistantActive] = useState(true);

  const assistantInactiveChange = () => {
    setAssistantActive((prev) => !prev);
  };

  useEffect(() => {
    resetEditorHighlights();
  }, [assistantActive]);

  return (
    <div className={classes.column}>
      {!loading && !_.isEmpty(allSuggestions) ? (
        <Stack className={classes.toggle} direction={'row'} gap={2} justifyContent={'center'} alignItems={'center'}>
          <PaiSwitch checked={assistantActive} onChange={assistantInactiveChange} />
          <Typography className={cx(classes.text, assistantActive ? 'active' : null)}>{t('showAssistant')}</Typography>
        </Stack>
      ) : null}
      <div className={classes.textGenerationSuggestions}>
        <Typography className={classes.title}>{t('suggestions')}</Typography>
        {loading ? <PaiDotLoader size="normal" /> : null}
        {!loading && !_.isNil(allSuggestions) && allSuggestions.length > 0 && assistantActive ? (
          <Stack direction={'column'} gap={{ xs: '16px', lg: '8px', xl: '16px' }}>
            {allSuggestions.map((suggestion, index) => (
              <SuggestionCard
                key={index}
                suggestion={suggestion}
                skipSuggestion={skipSuggestion}
                ignoreSuggestion={ignoreSuggestion}
                insertAtPosition={insertAtPosition}
                active={isSuggestionActive(suggestion.highlightId)}
                activateSuggestionCard={activateSuggestionCard}
              />
            ))}
          </Stack>
        ) : null}
        {!_.isNil(sidebarNotes) && sidebarNotes.length > 0 && (allSuggestions.length === 0 || !assistantActive) ? (
          <div style={{ visibility: loading ? 'hidden' : 'visible' }}>
            <NoteList sidebarNotes={sidebarNotes} onCategoryClicked={onSidebarNotesCategoryClicked} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  column: {
    position: 'relative',
    width: '280px',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    [theme.breakpoints.down('xl')]: {
      height: '640px',
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      height: 'unset',
      width: '100%',
    },
  },
  textGenerationSuggestions: {
    height: 'calc(100% - 16px)',
    padding: '8px 16px 16px 16px',
    overflow: 'auto',
  },
  title: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: '16px',
  },
  toggle: {
    position: 'absolute',
    top: '-32px',
  },
  text: {
    fontSize: theme.typography.pxToRem(11),
    '&.active': {
      color: theme.palette.primary.main,
    },
  },
}));

export default TextGenerationSuggestions;
